import { Alert } from '@creditornot/cb-alert';
import { Button, TextButton, IconButton } from '@creditornot/cb-button';
import { Cross, Plus, Trashbin } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Radio } from '@creditornot/cb-radio';
import R from 'ramda';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';

import WeekdayMultiSelect from './WeekdayMultiSelect';
import { WeeklyScheduleInputEntry } from './types';
import { checkForOverlappingTimePairs } from './utils';
import { DEFAULT_TIME_PAIR, EMPTY_WEEKLY_SCHEDULE_INPUT_ENTRY } from './values';
import TimePairInput from '../TimePairInput';

export type Props = {
  pristine: boolean;
  value: WeeklyScheduleInputEntry[];
  onAddEntry?: () => void;
  onChange: (value: WeeklyScheduleInputEntry[]) => void;
  onRemoveEntry?: () => void;
  select24HoursLabel?: string; // Used to override default opening hours -specific label if defined
};

const Entry = styled.div`
  & + & {
    padding-block-start: 24px;
    border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  }
`;

const WeekdaySelect = styled(WeekdayMultiSelect)`
  margin-block-end: 16px;
`;

const RadioButtons = styled.div`
  display: flex;
  margin-block-end: 16px;
  & > * + * {
    margin-inline-start: 16px;
  }
`;

const RadioLabel = styled(T.Body3)`
  color: ${cbSemanticCssVars.textPrimary};
`;

const TimePair = styled.div`
  margin-block-end: 16px;
  display: flex;
`;

const RemoveIcon = styled(Cross).attrs({})``;

const RemoveTime = styled(IconButton).attrs({
  size: 'small',
  variant: 'grey',
  background: 'staticWhite',
})`
  margin-inline-start: 8px;
`;

const DeleteIcon = styled(Trashbin)``;

const InfoMessage = styled(Alert).attrs({
  variant: 'info',
  size: 'small',
})`
  margin-block-start: 24px;
`;

const EntryButtons = styled.div`
  margin-block-end: 24px;
  display: flex;
`;

const AddTime = styled(TextButton)`
  margin-inline-end: 16px;
`;

const RemoveEntry = styled(TextButton).attrs({ size: 'small', variant: 'grey' })``;

const AddEntry = styled(Button).attrs({ size: 'small', variant: 'lightBlue' })``;

const CautionMessage = styled(Alert).attrs({
  variant: 'error',
  size: 'small',
})`
  margin-block-end: 16px;
  background: transparent;
`;

const WeeklyScheduleInput = ({
  pristine,
  value: entries,
  onAddEntry,
  onChange,
  onRemoveEntry,
  select24HoursLabel,
}: Props) => {
  const disabledWeekdays = entries.map(entry => (entry.open24Hours ? entry.daysOfWeek : [])).flat();
  const hasIncompleteEntries = !entries.every(entry => entry.daysOfWeek.length !== 0);

  return (
    <>
      {entries.map((entry, entryIndex) => {
        const hasOverlappingTimePairs = entry.open24Hours ? false : checkForOverlappingTimePairs(entry);
        return (
          <Entry key={entryIndex}>
            <WeekdaySelect
              disabledWeekdays={disabledWeekdays}
              value={entry.daysOfWeek}
              onChange={daysOfWeek => {
                onChange([...entries.slice(0, entryIndex), { ...entry, daysOfWeek }, ...entries.slice(entryIndex + 1)]);
              }}
            />
            <RadioButtons>
              <Radio
                checked={!entry.open24Hours}
                onChange={event => {
                  onChange(R.update(entryIndex, { ...entry, open24Hours: !event.target.checked }, entries));
                }}
              >
                <RadioLabel>
                  <FormattedMessage id="opening-hours.set-hours-manually" defaultMessage="Set hours manually" />
                </RadioLabel>
              </Radio>
              <Radio
                checked={entry.open24Hours}
                onChange={event => {
                  onChange(R.update(entryIndex, { ...entry, open24Hours: event.target.checked }, entries));
                }}
              >
                <RadioLabel>
                  {select24HoursLabel ? (
                    select24HoursLabel
                  ) : (
                    <FormattedMessage id="opening-hours.open-24-hours" defaultMessage="Open 24 hours" />
                  )}
                </RadioLabel>
              </Radio>
            </RadioButtons>
            {!entry.open24Hours && (
              <div>
                {entry.timePairs.map((timePair, timePairIndex) => {
                  return (
                    <TimePair key={timePairIndex}>
                      <TimePairInput
                        onChange={newTimePair => {
                          onChange(
                            R.update(
                              entryIndex,
                              { ...entry, timePairs: R.update(timePairIndex, newTimePair, entry.timePairs) },
                              entries,
                            ),
                          );
                        }}
                        value={timePair}
                      />
                      <RemoveTime
                        onClick={() => {
                          onChange(
                            R.update(
                              entryIndex,
                              { ...entry, timePairs: R.remove(timePairIndex, 1, entry.timePairs) },
                              entries,
                            ),
                          );
                        }}
                      >
                        <RemoveIcon />
                      </RemoveTime>
                    </TimePair>
                  );
                })}
              </div>
            )}
            {hasOverlappingTimePairs && (
              <CautionMessage>
                <FormattedMessage
                  id="opening-hours.hours-overlap-info"
                  defaultMessage="Your opening hours overlap with each other. Please check them before submitting."
                />
              </CautionMessage>
            )}
            <EntryButtons>
              {!entry.open24Hours && (
                <AddTime
                  type="button" // Prevent submit if used inside <form>
                  onClick={() => {
                    onChange(
                      R.update(entryIndex, { ...entry, timePairs: [...entry.timePairs, DEFAULT_TIME_PAIR] }, entries),
                    );
                  }}
                  icon={<Plus />}
                >
                  <FormattedMessage id="opening-hours.add-time" defaultMessage="Add time" />
                </AddTime>
              )}
              <RemoveEntry
                type="button" // Prevent submit if used inside <form>
                icon={<DeleteIcon />}
                onClick={() => {
                  onChange(entries.length > 1 ? R.remove(entryIndex, 1, entries) : [EMPTY_WEEKLY_SCHEDULE_INPUT_ENTRY]);
                  if (onRemoveEntry) {
                    onRemoveEntry();
                  }
                }}
              >
                <FormattedMessage id="opening-hours.remove-entry" defaultMessage="Remove entry" />
              </RemoveEntry>
            </EntryButtons>
          </Entry>
        );
      })}
      <AddEntry
        type="button" // Prevent submit if used inside <form>
        icon={<Plus />}
        onClick={() => {
          onChange([...entries, EMPTY_WEEKLY_SCHEDULE_INPUT_ENTRY]);
          if (onAddEntry) {
            onAddEntry();
          }
        }}
      >
        <FormattedMessage id="opening-hours.add-entry" defaultMessage="Add entry" />
      </AddEntry>
      {hasIncompleteEntries && !pristine && (
        <InfoMessage>
          <FormattedMessage id="opening-hours.incomplete-entries-info" defaultMessage="Some entries seem incomplete." />
        </InfoMessage>
      )}
    </>
  );
};

export default WeeklyScheduleInput;
